import { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { 
  LayoutDashboard, Users, Calendar, CreditCard, LogOut, Stethoscope, ClipboardList, 
  UserCog, BarChart3, CalendarClock, Building2, DollarSign, Book, ListOrdered, Package,
  Settings, FileText, ClipboardCheck, Smile, ClipboardPlus, Grid3X3
} from 'lucide-react';
import GlobalSearch from './GlobalSearch';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Layout = ({ children }) => {
  const { user, token, logout, canAccessBilling, canAccessClinical, isOwner, isDoctor, isReception } = useAuth();
  const navigate = useNavigate();
  const [tenantSettings, setTenantSettings] = useState(null);
  // Persist mode in localStorage
  const [activeMode, setActiveMode] = useState(() => {
    const saved = localStorage.getItem('mymedpal_mode');
    return saved || 'medical';
  });

  // Save mode to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('mymedpal_mode', activeMode);
  }, [activeMode]);

  useEffect(() => {
    if (token) {
      fetchTenantSettings();
    }
  }, [token]);

  const fetchTenantSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/tenant/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTenantSettings(response.data);
      // Set default mode based on what's enabled
      if (response.data.modules_enabled?.dental && !response.data.modules_enabled?.medical) {
        setActiveMode('dental');
      }
    } catch (error) {
      console.error('Failed to fetch tenant settings');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Check if user is cashier
  const isCashier = user?.role === 'cashier';
  const isInventoryManager = user?.role === 'inventory_manager';
  const isNurse = user?.role === 'nurse';
  const isDentist = user?.role === 'dentist';
  const isHygienist = user?.role === 'hygienist';
  const isDentalStaff = isDentist || isHygienist;

  // Check if modules are enabled
  const medicalEnabled = tenantSettings?.modules_enabled?.medical ?? true;
  const dentalEnabled = tenantSettings?.modules_enabled?.dental ?? false;
  const showModeToggle = medicalEnabled && dentalEnabled;

  // Build navigation items based on role and active mode
  const getMedicalNavItems = () => [
    { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, show: true },
    { path: '/queue', label: 'Patient Queue', icon: ListOrdered, show: isOwner || isReception || isDoctor || isNurse },
    { path: '/patients', label: 'Patients', icon: Users, show: !isCashier && !isInventoryManager },
    { path: '/appointments', label: isDoctor ? 'My Schedule' : 'Appointments', icon: CalendarClock, show: !isCashier && !isInventoryManager },
    { path: '/encounters', label: 'Encounters', icon: Calendar, show: canAccessClinical },
    { path: '/tasks', label: 'Tasks', icon: ClipboardList, show: !isCashier && !isInventoryManager },
    { path: '/billing', label: 'Billing', icon: CreditCard, show: canAccessBilling || isCashier },
    { path: '/outstanding-balances', label: 'Collections', icon: DollarSign, show: isCashier || isOwner },
    { path: '/inventory', label: 'Inventory', icon: Package, show: isOwner },
    { path: '/insurance-providers', label: 'Insurers', icon: Building2, show: isOwner },
    { path: '/productivity', label: 'Productivity', icon: BarChart3, show: isOwner },
    { path: '/staff', label: 'Staff', icon: UserCog, show: isOwner },
    { path: '/library', label: 'Library', icon: Book, show: true },
  ].filter(item => item.show);

  const getDentalNavItems = () => [
    { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, show: true },
    { path: '/dental/scheduling', label: 'Scheduling', icon: CalendarClock, show: true },
    { path: '/patients', label: 'Patients', icon: Users, show: !isCashier && !isInventoryManager },
    { path: '/dental/services', label: 'Services', icon: FileText, show: true },
    { path: '/dental/treatment-plans', label: 'Treatment Plans', icon: ClipboardPlus, show: !isCashier && !isInventoryManager },
    { path: '/dental/charting', label: 'Charting', icon: Grid3X3, show: !isCashier && !isInventoryManager },
    { path: '/dental/perio', label: 'Perio Chart', icon: BarChart3, show: !isCashier && !isInventoryManager },
    { path: '/tasks', label: 'Tasks', icon: ClipboardList, show: !isCashier && !isInventoryManager },
    { path: '/billing', label: 'Billing', icon: CreditCard, show: canAccessBilling || isCashier || isDentalStaff },
    { path: '/outstanding-balances', label: 'Collections', icon: DollarSign, show: isCashier || isOwner },
    { path: '/inventory', label: 'Inventory', icon: Package, show: isOwner },
    { path: '/staff', label: 'Staff', icon: UserCog, show: isOwner },
    { path: '/library', label: 'Library', icon: Book, show: true },
  ].filter(item => item.show);

  const navItems = activeMode === 'dental' && dentalEnabled ? getDentalNavItems() : getMedicalNavItems();

  // Get role display name
  const getRoleDisplay = (role) => {
    switch (role) {
      case 'owner': return 'Owner Admin';
      case 'doctor': return 'Doctor';
      case 'nurse': return 'Nurse';
      case 'reception': return 'Reception';
      case 'cashier': return 'Cashier';
      case 'inventory_manager': return 'Inventory Mgr';
      case 'dentist': return 'Dentist';
      case 'hygienist': return 'Hygienist';
      case 'dental_assistant': return 'Dental Asst';
      default: return role || 'User';
    }
  };

  return (
    <div className="flex min-h-screen" data-testid="app-layout">
      {/* Sidebar */}
      <aside className="sidebar" data-testid="sidebar">
        <div className="p-6 border-b border-slate-800">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
              activeMode === 'dental' ? 'bg-teal-600' : 'bg-blue-600'
            }`}>
              {activeMode === 'dental' ? (
                <Smile className="w-6 h-6 text-white" />
              ) : (
                <Stethoscope className="w-6 h-6 text-white" />
              )}
            </div>
            <div>
              <h1 className="text-lg font-bold text-white font-heading">My Med Pal</h1>
              <p className="text-xs text-slate-400">
                {activeMode === 'dental' ? 'Dental Practice' : 'Medical Practice'}
              </p>
            </div>
          </div>

          {/* Mode Toggle */}
          {showModeToggle && (
            <div className="mt-4 p-2 bg-slate-800 rounded-lg">
              <div className="flex items-center justify-between">
                <button
                  onClick={() => setActiveMode('medical')}
                  className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded text-sm font-medium transition-all ${
                    activeMode === 'medical' 
                      ? 'bg-blue-600 text-white' 
                      : 'text-slate-400 hover:text-white'
                  }`}
                >
                  <Stethoscope className="w-4 h-4" />
                  Medical
                </button>
                <button
                  onClick={() => setActiveMode('dental')}
                  className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded text-sm font-medium transition-all ${
                    activeMode === 'dental' 
                      ? 'bg-teal-600 text-white' 
                      : 'text-slate-400 hover:text-white'
                  }`}
                >
                  <Smile className="w-4 h-4" />
                  Dental
                </button>
              </div>
            </div>
          )}
        </div>

        <nav className="flex-1 py-6 overflow-y-auto scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-slate-800" data-testid="navigation">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
              className={({ isActive }) =>
                `sidebar-link ${isActive ? 'sidebar-link-active' : 'sidebar-link-inactive'}`
              }
            >
              <item.icon className="w-5 h-5" />
              {item.label}
            </NavLink>
          ))}

          {/* Settings link for owner */}
          {isOwner && (
            <NavLink
              to="/settings"
              data-testid="nav-settings"
              className={({ isActive }) =>
                `sidebar-link ${isActive ? 'sidebar-link-active' : 'sidebar-link-inactive'}`
              }
            >
              <Settings className="w-5 h-5" />
              Settings
            </NavLink>
          )}
        </nav>

        <div className="p-4 border-t border-slate-800">
          <div className="flex items-center gap-3 mb-4 px-2">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium text-white ${
              activeMode === 'dental' ? 'bg-teal-700' : 'bg-slate-700'
            }`}>
              {user?.full_name?.charAt(0) || 'U'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.full_name}</p>
              <p className="text-xs text-slate-400">{getRoleDisplay(user?.role)}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            data-testid="logout-btn"
            className="sidebar-link sidebar-link-inactive w-full"
          >
            <LogOut className="w-5 h-5" />
            Logout
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="main-content" data-testid="main-content">
        {/* Global Search Bar */}
        <div className="sticky top-0 z-40 bg-slate-50 border-b border-slate-200 px-6 py-3">
          <GlobalSearch />
        </div>
        <div className="p-0">
          {children}
        </div>
      </main>
    </div>
  );
};

export default Layout;
